"use client";

/**
 * "What we offer" - a modern, asymmetric BENTO (Apple-style): rounded-3xl
 * tiles of deliberately varied sizes, image-forward where it counts, with
 * bold short titles and lots of breathing room.
 *
 *   Row 1:  Listings (wide, real-photo feature)   ·   Analyze (animation)
 *   Row 2:  Map  ·  Residences  ·  Trends  ·  Chat
 *   Row 3:  the tools strip
 *
 * Listings is image-forward - a 2x2 collage of REAL listing photos with
 * the title over a gradient (the "real UI" feel). Analyze, Map,
 * Residences, Trends and Chat keep their CSS animations. The come-back
 * tools (Compare / Alerts / Watchlist) are a single grouped tile so they
 * read as a tidy "and more" cluster, not a flat icon row.
 *
 * Dark theme: bare gray/white classes auto-flip; the soft ring carries
 * explicit hex/dark pairs, hovers stay grey (the quiet UI). No tinted "stage well" - the
 * tiles sit directly on the page like an Apple bento.
 */

import { type ComponentType } from "react";
import Link from "@/lib/localized-link";
import { useT } from "@/lib/locale-context";
import { trackEvent } from "@/lib/tracking";
import type { TranslationKey } from "@/lib/i18n";
import type { Intent } from "@/lib/use-personal";
import AnalyzeAnimation from "./animations/AnalyzeAnimation";
import ListingsAnimation from "./animations/ListingsAnimation";
import MapAnimation from "./animations/MapAnimation";
import TrendsAnimation from "./animations/TrendsAnimation";
import ChatFlowAnimation from "./animations/ChatFlowAnimation";
import ResidencesAnimation from "./animations/ResidencesAnimation";
import {
  ArrowRight, BadgeCheck, ShieldCheck, FileCheck,
} from "lucide-react";
import Reveal from "@/components/Reveal";
import { FIRST_PARTY_LISTINGS } from "@/lib/flags";
import { CompareAnim, PropertiesAnim, AlertsAnim, WatchlistAnim, InvestAnim, CalcAnim } from "./animations/ToolAnimations";

// Shared tile chrome: soft ring, big radius. Hover only darkens the ring:
// no lift, no shadow, no glow (2026-10-19, a visitor read the stacked hover
// as AI-made).
const TILE =
  "no-scale group relative flex h-full flex-col overflow-hidden rounded-3xl bg-white ring-1 ring-gray-200/70 dark:ring-white/10 transition duration-300 ease-out active:scale-[0.98] hover:ring-gray-300 dark:hover:ring-white/20";

/* ─── Animated flow tile: Analyze / Map / Residences / Trends / Chat ─── */
function FlowTile({
  flow,
  href,
  Anim,
  title,
  desc,
  span,
  delay = 0,
}: {
  flow: string;
  href: string;
  Anim: ComponentType;
  title: string;
  desc: string;
  span: string;
  delay?: number;
}) {
  return (
    <Reveal className={span} delay={delay}>
    <Link
      href={href}
      onClick={() => trackEvent("home.flow.click", { flow })}
      className={`${TILE} p-5 sm:p-6`}
    >
      <ArrowRight className="absolute top-5 right-5 h-4 w-4 text-gray-300 transition-all duration-200 group-hover:translate-x-0.5 group-hover:text-gray-500 dark:text-white/20" />
      <div className="mb-4 overflow-hidden">
        <Anim />
      </div>
      <h3 className="text-lg font-bold tracking-tight text-gray-900 underline-offset-2 group-hover:underline">
        {title}
      </h3>
      <p className="mt-1.5 text-sm text-gray-600 leading-relaxed">{desc}</p>
    </Link>
    </Reveal>
  );
}

/* ─── Listings feature tile: our own listings-feed animation + copy ─────
   Image-free on purpose - earlier we used real listing thumbnails, but
   those are third-party and change per render. This uses the in-house
   ListingsAnimation (a stylized results feed) so the tile is ours and
   stable, in a wide horizontal layout that fills the feature column. */
function ListingsTile({ title, desc, span }: { title: string; desc: string; span: string }) {
  const t = useT();
  return (
    <Reveal className={span}>
    <Link
      href="/listings"
      onClick={() => trackEvent("home.flow.click", { flow: "listings" })}
      className={`${TILE} p-5 sm:p-6 sm:flex-row sm:items-center sm:gap-6`}
    >
      <ArrowRight className="absolute top-5 right-5 h-4 w-4 text-gray-300 transition-all duration-200 group-hover:translate-x-0.5 group-hover:text-gray-500 dark:text-white/20" />
      {/* The framed well is sm+ only: on mobile the tile's corner arrow
          sat right on the well's ring, which read as a collision - the
          illustration floats frameless there instead. */}
      <div className="mb-4 sm:mb-0 sm:w-1/2 overflow-hidden rounded-2xl sm:bg-gray-50 sm:ring-1 sm:ring-gray-100 sm:dark:bg-white/[0.03] sm:dark:ring-white/10 flex items-center justify-center">
        <ListingsAnimation />
      </div>
      <div className="sm:w-1/2">
        <h3 className="text-xl sm:text-2xl font-bold tracking-tight text-gray-900 underline-offset-2 group-hover:underline">
          {title}
        </h3>
        <p className="mt-2 text-sm text-gray-600 leading-relaxed">{desc}</p>
        {/* First-party listings are the trust story: every check is a
            real gate (moderation queue, OTP phone, deed cross-check).
            Hidden while own-listing creation is cut (lib/flags.ts) -
            without the wizard the claims would describe nothing. */}
        {/* Trust chips render ONLY with first-party listings live - each
            claim (moderation queue, OTP phone, deed cross-check) needs
            the wizard behind it. While own-listing creation is cut
            (lib/flags.ts), the tile is title + desc only: no chips, per
            owner call 2026-07-20 (the browse-value chips read as filler
            without a first-party story). */}
        {FIRST_PARTY_LISTINGS && (
          <>
            <p className="mt-4 text-xs font-semibold text-gray-500">
              {t("home.tools.listings.own")}
            </p>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {([
                [ShieldCheck, "home.tools.listings.chip1"],
                [BadgeCheck, "home.tools.listings.chip2"],
                [FileCheck, "home.tools.listings.chip3"],
              ] as const).map(([Icon, key]) => (
                <span
                  key={key}
                  className="inline-flex items-center gap-1 rounded-full bg-emerald-50 dark:bg-emerald-950/50 px-2 py-0.5 text-xs font-semibold text-emerald-700 dark:text-emerald-400"
                >
                  <Icon className="h-3 w-3" />
                  {t(key as TranslationKey)}
                </span>
              ))}
            </div>
          </>
        )}
      </div>
    </Link>
    </Reveal>
  );
}

/* ─── Grouped "tools" tile: Compare / Alerts / Watchlist ──────────────── */
interface ToolDef {
  tool: string;
  href: string;
  // Either an animated illustration (retention tools) or a static lucide
  // icon (the calculators, which have no bespoke animation).
  Anim?: ComponentType;
  Icon?: ComponentType<{ className?: string }>;
  titleKey: TranslationKey;
  descKey?: TranslationKey;
}

const TOOLS: ToolDef[] = [
  { tool: "properties", href: "/properties", Anim: PropertiesAnim, titleKey: "home.tools.properties.title", descKey: "myproperties.tiles.desc" },
  { tool: "invest", href: "/invest", Anim: InvestAnim, titleKey: "home.retention.invest.title", descKey: "home.retention.invest.desc" },
  { tool: "compare", href: "/compare", Anim: CompareAnim, titleKey: "home.retention.compare.title", descKey: "home.retention.compare.desc" },
  { tool: "alerts", href: "/alerts", Anim: AlertsAnim, titleKey: "home.retention.alerts.title", descKey: "home.retention.alerts.desc" },
  { tool: "watchlist", href: "/watchlist", Anim: WatchlistAnim, titleKey: "home.retention.watchlist.title", descKey: "home.retention.watchlist.desc" },
];

/* One compact tool tile: illustration on top, short title below. Kept
 * deliberately small (no description) so the whole strip is a single low
 * row, and it scales - adding a tool just flows into the next cell. */
function ToolChip({ tool, href, Anim, Icon, titleKey }: ToolDef) {
  const t = useT();
  return (
    <Link
      href={href}
      onClick={() => trackEvent("home.tool.click", { tool })}
      className={`${TILE} items-center text-center p-3 sm:p-4 gap-2`}
    >
      <span className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden">
        {Anim ? <Anim /> : Icon ? <Icon className="h-5 w-5 text-gray-400 transition-colors group-hover:text-gray-700" /> : null}
      </span>
      <span className="text-xs sm:text-sm font-bold leading-tight text-gray-900 underline-offset-2 group-hover:underline">
        {t(titleKey)}
      </span>
    </Link>
  );
}

/* Full-width tool strip: a single low row on desktop (3-up on mobile) that
 * WRAPS as tools are added, so the bento keeps its varied tile sizes
 * without one tile ballooning - and stays compact. */
// ONE plain chip for all calculators (owner call: the grouped link-list
// tile towered over its row on every breakpoint). It lands on /calculate,
// whose persistent segmented nav fans out to the sub-calculators - and
// as the 6th chip it squares the strip into two clean rows of 3.
function CalcGroupTile() {
  const t = useT();
  return (
    <Link
      href="/calculate"
      onClick={() => trackEvent("home.tool.click", { tool: "calculators" })}
      className={`${TILE} items-center text-center p-3 sm:p-4 gap-2`}
    >
      <span className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden">
        <CalcAnim />
      </span>
      <span className="text-xs sm:text-sm font-bold leading-tight text-gray-900 underline-offset-2 group-hover:underline">
        {t("home.tools.calcs")}
      </span>
    </Link>
  );
}

// The tools strip follows what the visitor is doing (lib/use-personal):
// an investor's yield ranking first, an owner's properties first.
const TOOL_ORDER: Partial<Record<Intent, string[]>> = {
  buy: ["alerts", "watchlist", "compare", "invest", "properties"],
  rent: ["alerts", "watchlist", "compare", "properties", "invest"],
  invest: ["invest", "alerts", "compare", "watchlist", "properties"],
  sell: ["properties", "compare", "alerts", "invest", "watchlist"],
  rent_out: ["properties", "invest", "compare", "alerts", "watchlist"],
  agent: ["properties", "compare", "alerts", "watchlist", "invest"],
};

function ToolsStrip({ intent }: { intent: Intent | null }) {
  const order = intent ? TOOL_ORDER[intent] : undefined;
  const tools = order ? [...TOOLS].sort((a, b) => order.indexOf(a.tool) - order.indexOf(b.tool)) : TOOLS;
  return (
    <Reveal className="lg:col-span-12" delay={120}>
      {/* Six tiles (five tools + the calculator group; the finder was parked 2026-09-20): 3+3 on phones
          and tablets, one full row of seven on desktop, so no tile ever
          sits alone in a row. */}
      <div className="grid grid-cols-3 gap-2 sm:gap-4 lg:grid-cols-6">
        {tools.map((tool) => (
          <ToolChip key={tool.tool} {...tool} />
        ))}
        <CalcGroupTile />
      </div>
    </Reveal>
  );
}

export default function HomeCoreFlowsRow({
  intent = null,
}: {
  intent?: Intent | null;
} = {}) {
  const t = useT();
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
      <Reveal>
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 tracking-tight text-center mb-8 sm:mb-10">
          {t("home.flows.title")}
        </h2>
      </Reveal>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 sm:gap-4">
        {/* Row 1 - Listings feature (wide) + Analyze. */}
        <ListingsTile
          span="lg:col-span-7"
          title={t("home.tools.listings.title")}
          desc={t("home.tools.listings.desc")}
        />
        <FlowTile
          span="lg:col-span-5"
          delay={80}
          flow="analyze"
          href="/analyze"
          Anim={AnalyzeAnimation}
          title={t("flow.analyze.title")}
          desc={t("home.flow.analyze.short")}
        />

        {/* Row 2 - four feature tiles: Map + Residences + Trends + AI
            chat (the residences row became a tile, 2026-10-29). The
            residences tile is an animation like its neighbours: its real
            rows with building pictures read as a different drawing among
            the stylized mocks (owner, 2026-11-01). */}
        <FlowTile
          span="lg:col-span-3"
          flow="map"
          href="/map"
          Anim={MapAnimation}
          title={t("flow.map.title")}
          desc={t("home.flow.map.short")}
        />
        <FlowTile
          span="lg:col-span-3"
          delay={50}
          flow="residences"
          href="/residences"
          Anim={ResidencesAnimation}
          title={t("home.flow.residences.title")}
          desc={t("home.flow.residences.short")}
        />
        <FlowTile
          span="lg:col-span-3"
          delay={100}
          flow="trends"
          href="/trends"
          Anim={TrendsAnimation}
          title={t("flow.trends.title")}
          desc={t("home.flow.trends.short")}
        />
        <FlowTile
          span="lg:col-span-3"
          delay={150}
          flow="chat"
          href="/chat"
          Anim={ChatFlowAnimation}
          title={t("home.retention.chat.title")}
          desc={t("home.retention.chat.desc")}
        />

        {/* Row 3 - compact tool strip that wraps as tools are added. */}
        <ToolsStrip intent={intent} />
      </div>
    </section>
  );
}
